import { HighchartsChart, IconFa } from '@/components/wm';
import { cn } from '@/utils';
import React, { useMemo } from 'react';

import { axisDecimals, chartColors, objectiveProgress, objectiveUnit } from '../utils';

const paletteVariants = {
    primary: 'tw-bg-primary tw-text-secondary',
    secondary: 'tw-bg-secondary tw-text-gray-200',
    simple: 'tw-bg-white tw-border tw-border-solid tw-border-border tw-text-secondary'
};

const iconVariants = {
    primary: 'tw-text-secondary',
    secondary: 'tw-text-gray-200',
    simple: 'tw-text-secondary'
};

const LegendSwatch = ({ color }) => (
    <span className="tw-h-2 tw-w-2 tw-flex-shrink-0" style={{ backgroundColor: color }}/>
);

// Reads WiseMetering.objectives, loaded once at start-up, so it does not update
// while the page is open. `sample`, when given, stands in for the objective: the
// type picker shows each widget with made-up values.
export default function ObjectiveWidget({ sample, widget }) {
    const
        objective = WiseMetering.objectives.get(widget.data.objectiveId),
        indicator = objective?.indicator(),
        palette = widget.graphical.palette || 'primary',
        period = widget.data.period || 'ytd',
        year = sample?.year ?? objective?.get('year'),
        isConsumption = sample?.isConsumption ?? objective?.isConsumption();

    const
        decimals = widget.data.decimals,
        icon = WiseMetering.icons.getIconFromKind(indicator?.get('kind_slug')) || 'info',
        unit = sample?.unit ?? (objective ? objectiveUnit(objective) : ''),
        unitScale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;

    const progress = useMemo(
        () => sample?.progress ?? (objective && objectiveProgress(objective, period)),
        [objective, period, sample]
    );

    // Memoised: HighchartsChart redraws on every new options object.
    const options = useMemo(() => {
        if (!progress) {
            return null;
        }

        const
            colors = chartColors(palette),
            months = i18next.t('highcharts.shortMonths', { returnObjects: true }),
            format = value => formatValue(value, unit, decimals, unitScale),
            // MTD charts only the current month (the first, before the year
            // starts), YTD the whole year.
            first = period === 'mtd' ? Math.max(progress.currentMonth, 0) : 0,
            last = period === 'mtd' ? first + 1 : 12;

        return {
            chart: {
                backgroundColor: 'transparent',
                spacing: [8, 8, 0, 0]
            },
            credits: { enabled: false },
            exporting: { enabled: false },
            legend: { enabled: false },
            plotOptions: {
                // Each month's target and actual touching, with the gap between
                // months. Not crisp: rounding to whole pixels can open a hairline
                // between the two. No `maxPointWidth`: a capped column is centred
                // in its slot, which opens a gap; a lone MTD month is narrowed by
                // its padding instead.
                column: {
                    borderWidth: 0,
                    crisp: false,
                    groupPadding: period === 'mtd' ? 0.35 : 0.08,
                    pointPadding: 0
                }
            },
            // The target first, so it is the left column of each month's pair, in
            // the blue the opportunities use for pending.
            series: [{
                color: WiseMetering.utils.getColorByState('pending'),
                data: progress.targets.slice(first, last),
                name: i18next.t('glossary.objective').capitalize(),
                type: 'column'
            }, {
                color: colors.series,
                data: progress.real.slice(first, last),
                name: year,
                type: 'column'
            }],
            title: { text: null },
            tooltip: {
                shared: true,
                formatter: function() {
                    const
                        month = first + this.points[0].point.index,
                        real = progress.real[month],
                        target = progress.targets[month],
                        lines = [`${months[month]} ${year}`];

                    if (real !== null) {
                        lines.push(`${year}: <b>${format(real)}</b>`);
                    }

                    lines.push(`${i18next.t('glossary.objective').capitalize()}: <b>${format(target)}</b>`);

                    return lines.join('<br/>');
                }
            },
            xAxis: {
                categories: months.slice(first, last),
                labels: { style: { color: colors.text } },
                lineColor: colors.text,
                tickColor: colors.text
            },
            yAxis: {
                gridLineColor: colors.grid,
                gridLineWidth: 1,
                labels: {
                    formatter: function() {
                        return formatValue(this.value, unit, axisDecimals(this.axis.tickPositions, unit, decimals, unitScale), unitScale);
                    },
                    style: { color: colors.text }
                },
                title: { text: null }
            }
        };
    }, [decimals, palette, period, progress, unit, unitScale, year]);

    const
        deviation = progress?.deviation,
        hasDeviation = deviation !== null && deviation !== undefined && progress.actual > 0;

    return (
        <div
            className={cn(
                paletteVariants[palette],
                'tw-flex tw-flex-col tw-gap-1 tw-rounded-sm tw-px-4 tw-pt-4',
                'tw-h-full tw-w-full tw-overflow-hidden'
            )}
            id={widget.id}
        >
            <div>
                <div className="tw-flex tw-items-center tw-justify-between">
                    {/* The period goes with the title, so it follows a change of period */}
                    <div className="tw-text-base tw-font-medium tw-leading-none">
                        {[widget.title, `(${period.toUpperCase()})`].filter(Boolean).join(' ')}
                    </div>
                    <IconFa color={iconVariants[palette]} icon={icon} size="medium"/>
                </div>
                <div className="tw-text-md tw-mt-0">
                    {widget.subtitle || ''}
                </div>
            </div>
            {!progress ? (
                <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-text-sm tw-pb-4">
                    {i18next.t('ui.no_data_available')}
                </div>
            ) : (
                <>
                    {/* One line, with each figure's swatch, in its columns' colour,
                        between them: it doubles as the chart's legend. Wraps in a
                        narrow widget. */}
                    <div className="tw-flex tw-items-end tw-justify-between tw-gap-2">
                        <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-text-xs">
                            <LegendSwatch color={chartColors(palette).series}/>
                            <span className="tw-whitespace-nowrap">
                                {i18next.t(isConsumption ? 'common.consumption' : 'common.cost').capitalize()}: {formatValue(progress.actual, unit, decimals, unitScale)}
                            </span>
                            <LegendSwatch color={WiseMetering.utils.getColorByState('pending')}/>
                            <span className="tw-whitespace-nowrap">
                                {i18next.t('glossary.objective').capitalize()} ({period.toUpperCase()}): {formatValue(progress.target, unit, decimals, unitScale)}
                            </span>
                        </div>
                        {hasDeviation && (
                            <div
                                className="tw-flex-shrink-0 tw-rounded-sm tw-px-2 tw-py-1 tw-text-sm tw-font-bold tw-text-white"
                                style={{ backgroundColor: WiseMetering.SemaphorColors[deviation > 0 ? 'red' : 'green'] }}
                            >
                                {deviation > 0 ? '+' : ''}{formatValue(deviation * 100, '%', 1)}
                            </div>
                        )}
                    </div>
                    {/* Out of the flow, so the card sizes the chart, not the reverse */}
                    <div className="tw-relative tw-flex-1 tw-min-h-0">
                        <div className="tw-absolute tw-inset-0 tw-pb-2">
                            <HighchartsChart options={options}/>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
